import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';

// ── Types ──

export interface KioskTestimonial {
  id: string;
  name: string;
  text: string;
  rating: number;
  date: string;
  service: string;
}

export interface KioskConfig {
  businessId: string;
  businessName: string;
  slug: string;
  accentColor: string;
  logoUrl: string | null;
  coverPhotoUrl: string | null;
  headline: string | null;
  description: string | null;
  socialLinks: Record<string, string> | null;
  cancellationPolicy: string | null;
  fontMood: 'clean' | 'elegant' | 'bold' | 'vintage';
  showPrices: boolean;
  kioskGreeting: string;
  kioskAutoResetSeconds: number;
  showOperators: boolean;
  operatorSelectionMode: 'optional' | 'required' | 'hidden';
  allowWalkIns: boolean;
  address: string | null;
  city: string | null;
  province: string | null;
  phone: string | null;
  testimonials: KioskTestimonial[];
  showTestimonials: boolean;
  industry: string | null;
  isDemo: boolean;
}

export interface KioskService {
  id: string;
  name: string;
  duration_minutes: number;
  price: number;
  description?: string | null;
  intro_enabled?: boolean;
  intro_price?: number | null;
  intro_label?: string | null;
}

export interface KioskOperator {
  id: string;
  name: string;
  chair_name: string | null;
  resource_id: string;
  bio?: string | null;
  avatar_url?: string | null;
}

export interface KioskSlot {
  start: string;
  end: string;
}

export interface KioskHours {
  day_of_week: number;
  is_open: boolean;
  open_time: string;
  close_time: string;
}

// ── Font mood → font family ──

export function fontMoodToFamily(mood: string | null | undefined): string {
  switch (mood) {
    case 'elegant': return "'Georgia', 'Times New Roman', serif";
    case 'bold': return "'Impact', 'Arial Black', sans-serif";
    case 'vintage': return "'Courier New', monospace";
    default: return "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
  }
}

// ── Hook: resolve slug → all data (single RPC) ──

export function useKioskInit(slug: string | undefined) {
  const [config, setConfig] = useState<KioskConfig | null>(null);
  const [services, setServices] = useState<KioskService[]>([]);
  const [operators, setOperators] = useState<KioskOperator[]>([]);
  const [hours, setHours] = useState<KioskHours[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!slug) { setError('No booking page specified'); setLoading(false); return; }

    (async () => {
      try {
        const { data, error: rpcErr } = await supabase.rpc('get_kiosk_config', { p_slug: slug });
        if (!mountedRef.current) return;
        if (rpcErr || !data) { setError('This booking page doesn\'t exist'); setLoading(false); return; }
        if (data.error) { setError(data.error); setLoading(false); return; }

        const bp = data.booking_page ?? {};
        const biz = data.business ?? {};
        const branding = data.branding ?? {};

        if (!bp.kiosk_enabled) { setError('Online booking is not available for this business'); setLoading(false); return; }

        setConfig({
          businessId: biz.id ?? '',
          businessName: biz.name ?? 'Business',
          slug,
          accentColor: branding.accent_color ?? bp.accent_color ?? '#0F6CBD',
          logoUrl: branding.logo_url ?? bp.logo_url ?? null,
          coverPhotoUrl: bp.cover_photo_url ?? null,
          headline: bp.headline ?? null,
          description: bp.description ?? null,
          socialLinks: bp.social_links && Object.keys(bp.social_links).length > 0 ? bp.social_links : null,
          cancellationPolicy: bp.cancellation_policy ?? null,
          fontMood: bp.font_mood ?? 'clean',
          showPrices: bp.show_prices ?? true,
          kioskGreeting: bp.kiosk_greeting ?? 'Welcome! Book your appointment.',
          kioskAutoResetSeconds: bp.kiosk_auto_reset_seconds ?? 15,
          showOperators: bp.show_operators ?? false,
          operatorSelectionMode: bp.operator_selection_mode ?? 'optional',
          allowWalkIns: bp.allow_walk_ins ?? true,
          address: biz.address ?? null,
          city: biz.city ?? null,
          province: biz.province ?? null,
          phone: biz.phone ?? null,
          testimonials: Array.isArray(bp.testimonials) ? bp.testimonials : [],
          showTestimonials: bp.show_testimonials ?? true,
          industry: biz.industry ?? null,
          isDemo: biz.is_demo ?? false,
        });

        setServices(((data.services ?? []) as any[]).map(s => ({
          id: s.id, name: s.name, duration_minutes: s.duration_minutes,
          price: s.price, description: s.description ?? null,
          intro_enabled: s.intro_enabled ?? false,
          intro_price: s.intro_price ?? null,
          intro_label: s.intro_label ?? null,
        })));

        setOperators(((data.operators ?? []) as any[]).map(op => ({
          id: op.user_id,
          name: op.display_name || `${op.first_name ?? ''} ${op.last_name ?? ''}`.trim() || 'Unknown',
          chair_name: op.chair_name ?? null,
          resource_id: op.resource_id,
          bio: op.bio ?? null,
          avatar_url: op.avatar_url ?? null,
        })));

        setHours(((data.hours ?? []) as any[]).map(h => ({
          day_of_week: h.day_of_week, is_open: h.is_open,
          open_time: h.open_time, close_time: h.close_time,
        })));

        setLoading(false);
      } catch {
        if (mountedRef.current) { setError('Something went wrong. Please try again.'); setLoading(false); }
      }
    })();

    return () => { mountedRef.current = false; };
  }, [slug]);

  return { config, services, operators, hours, loading, error };
}

// ── Hook: fetch availability for a date ──

export function useAvailability(slug: string | undefined, date: string, serviceId: string | null, operatorId: string | null) {
  const [slots, setSlots] = useState<KioskSlot[]>([]);
  const [closed, setClosed] = useState(false);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  const fetch = useCallback(async () => {
    if (!slug || !serviceId) return;
    setLoading(true); setClosed(false);
    try {
      const { data, error: err } = await supabase.rpc('get_available_slots', {
        p_slug: slug, p_service_id: serviceId, p_date: date, p_assigned_id: operatorId,
      });
      if (!mountedRef.current) return;
      if (err || !data) { setSlots([]); setLoading(false); return; }
      if (data.error) {
        if (data.error === 'Closed on this day') setClosed(true);
        setSlots([]); setLoading(false); return;
      }
      setSlots((data.slots ?? []) as KioskSlot[]);
    } catch { if (mountedRef.current) setSlots([]); }
    finally { if (mountedRef.current) setLoading(false); }
  }, [slug, date, serviceId, operatorId]);

  useEffect(() => { mountedRef.current = true; fetch(); return () => { mountedRef.current = false; }; }, [fetch]);

  return { slots, closed, loading, refresh: fetch };
}

// ── Query: check if client is new to this business ──

export async function checkIsNewClient(businessId: string, identifier: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('check_is_new_client', {
      p_business_id: businessId, p_client_identifier: identifier,
    });
    if (error) return true; // benefit of the doubt
    return data === true;
  } catch { return true; }
}

// ── Query: dynamic price for a service at a given date/time ──

export interface DynamicPriceResult {
  price: number;
  base_price: number;
  is_dynamic: boolean;
  reason: string;
  savings: number;
  min_price?: number;
  max_price?: number;
  fill_rate?: number;
}

export async function getDynamicPrice(
  serviceId: string, date: string, time: string, businessId: string,
): Promise<DynamicPriceResult | null> {
  try {
    const { data, error } = await supabase.rpc('get_dynamic_price', {
      p_service_id: serviceId, p_booking_date: date, p_booking_time: time, p_business_id: businessId,
    });
    if (error || !data) return null;
    return data as DynamicPriceResult;
  } catch { return null; }
}

// ── Mutation: create booking ──

export async function createKioskBooking(params: {
  slug: string; serviceId: string; date: string; startTime: string;
  clientName: string; clientPhone: string; notes?: string;
}) {
  const { data, error } = await supabase.rpc('create_public_booking', {
    p_slug: params.slug, p_service_id: params.serviceId, p_date: params.date,
    p_start_time: params.startTime, p_client_name: params.clientName,
    p_client_phone: params.clientPhone, p_notes: params.notes ?? '',
  });
  if (error) return { success: false, error: error.message };
  if (data && !data.success) return { success: false, error: data.error };
  return { success: true, data };
}
